/**
 * taskOwnership — RW-3801: whose a listing / buyer / transaction / contact task
 * (Firestore `tasks` subcollections) is.
 *
 * The owner-scoped read (lib/taskQueries) is everything I created PLUS
 * everything assigned to me — the CG rule's two predicates. That union is the
 * right READ, but not the right "mine": a task I created and handed to an
 * assistant is theirs to do. It counted in my Overdue and Due Today and sat in
 * "Mine" as if I owed it.
 *
 *   - mine:      assigned to me, or created by me and assigned to nobody;
 *   - delegated: created by me and assigned to someone else.
 *
 * The one rule for Other Tasks, Home, Focus Mode, the nav badge and the task
 * inbox (lib/taskInbox fromOtherTask), so they can never disagree.
 */

/** The assignee's uid, whichever shape assignedTo has; null when nobody. */
export function taskAssigneeUid(t) {
  const a = t && t.assignedTo;
  if (!a) return null;
  if (typeof a === 'string') return a;
  if (Array.isArray(a)) return a.length ? a : null;
  return a.userId || null;
}

const assignedTo = (assignee, uid) => (Array.isArray(assignee) ? assignee.includes(uid) : assignee === uid);

/** Mine to do: assigned to me, or created by me and nobody else's. */
export function isMyTask(t, uid) {
  if (!t || !uid) return false;
  const assignee = taskAssigneeUid(t);
  return assignee ? assignedTo(assignee, uid) : t.createdBy === uid;
}

/** I created it and handed it to someone else — theirs to do. */
export function isDelegatedByMe(t, uid) {
  if (!t || !uid || t.createdBy !== uid) return false;
  const assignee = taskAssigneeUid(t);
  return !!assignee && !assignedTo(assignee, uid);
}
